import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    Switch,
    Tabs,
    Tab,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    alpha,
    useTheme,
    Fade,
    Grow,
    Avatar,
    Stack,
    IconButton,
    LinearProgress,
    Rating,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    EditOutlined as EditIcon,
    LockPersonOutlined as LockPersonIcon,
    FileDownloadOutlined as FileDownloadIcon,
    PhoneOutlined as PhoneIcon,
    LocationOnOutlined as LocationIcon,
    EmailOutlined as EmailIcon,
    EmojiEventsOutlined as AwardIcon,
    AssignmentOutlined as DocIcon,
    ArrowBack as BackIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOJTMonitorings, getOJTEvaluation } from '../Apis/IndustryConnectApis';

const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 24,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#22C55E',
                opacity: 1,
                border: 0,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
    },
    '& .MuiSwitch-track': {
        borderRadius: 24 / 2,
        backgroundColor: '#E2E8F0',
        opacity: 1,
    },
}));

const DetailChip = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2.5, borderRadius: 5, border: '1px solid #F1F5F9', bgcolor: '#F8FAFC' }}>
        <Avatar sx={{ bgcolor: 'white', color: '#2563EB', width: 44, height: 44, border: '1px solid #E2E8F0' }}>{icon}</Avatar>
        <Box>
            <Typography variant="caption" fontWeight="800" color="#64748B" display="block">{label}</Typography>
            <Typography variant="body2" fontWeight="1000" color="#0F172A">{value}</Typography>
        </Box>
    </Box>
);

const SectionHeader = ({ title, action }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="1000" color="#0F172A">{title}</Typography>
        {action}
    </Stack>
);

const ProfileSidebar = ({ student }) => {
    const theme = useTheme();
    return (
        <Stack spacing={3}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0', bgcolor: 'white' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                    <Typography variant="caption" fontWeight="1000" sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.08), px: 2, py: 0.5, borderRadius: 2 }}>
                        {student.id || 'STU10123'}
                    </Typography>
                    <IOSSwitch defaultChecked />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 4 }}>
                    <Avatar
                        src={student.avatar}
                        sx={{ width: 120, height: 120, borderRadius: 6, mb: 3, border: '6px solid #F8FAFC', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.05)' }}
                    />
                    <Typography variant="h5" fontWeight="1000" color="#0F172A" sx={{ mb: 0.5 }}>{student.name || 'Yugendar Reddy'}</Typography>
                    <Typography variant="body2" color="#64748B" fontWeight="800">{student.trade || 'Computer operator'} • {student.batch || 'Batch 2023'}</Typography>
                    <Chip
                        label="Verified Trainee"
                        size="small"
                        color="success"
                        sx={{ mt: 2, fontWeight: '1000', borderRadius: 2, fontSize: '0.65rem' }}
                    />
                </Box>

                <Divider sx={{ mb: 4, borderStyle: 'dashed' }} />

                <Stack spacing={2.5}>
                    <Typography variant="subtitle2" fontWeight="1000" color="#0F172A">Registry Details</Typography>
                    {[
                        { label: 'System Roll', value: '001 / REG-A' },
                        { label: 'Identification', value: 'Male | O+' },
                        { label: 'Natal Day', value: '29 April, 1994' },
                        { label: 'Linguistic Profile', value: 'English, Telugu' },
                    ].map((info, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="#64748B" fontWeight="800">{info.label}</Typography>
                            <Typography variant="caption" fontWeight="1000" color="#1E293B">{info.value}</Typography>
                        </Box>
                    ))}
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="1000" color="#0F172A" sx={{ mb: 3 }}>Quick Actions</Typography>
                <Stack spacing={2}>
                    <Button fullWidth variant="outlined" startIcon={<LockPersonIcon />} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold', borderColor: '#E2E8F0', color: '#1E293B' }}>Portal Credentials</Button>
                    <Button fullWidth variant="contained" startIcon={<DocIcon />} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold', bgcolor: '#0F172A' }}>Generate ID Card</Button>
                </Stack>
            </Paper>
        </Stack>
    );
};

const ExamsAndResults = () => {
    const subjects = [
        { name: 'Theory of Machines', score: 98, max: 100, min: 35 },
        { name: 'Applied Mathematics', score: 92, max: 100, min: 35 },
        { name: 'Professional English', score: 88, max: 100, min: 35 },
        { name: 'Workshop Science', score: 94, max: 100, min: 35 },
    ];

    return (
        <Stack spacing={4}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                <SectionHeader
                    title="Academic Performance"
                    action={<Chip label="Phase 1: Pass" color="success" sx={{ fontWeight: '1000', borderRadius: 2 }} />}
                />

                <Grid container spacing={3} sx={{ mb: 5 }}>
                    <Grid item size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="#64748B" fontWeight="800">CUMULATIVE GPA</Typography>
                        <Typography variant="h4" fontWeight="1000" color="#0F172A">9.2</Typography>
                    </Grid>
                    <Grid item size={{ xs: 6, md: 3 }}>
                        <Typography variant="caption" color="#64748B" fontWeight="800">TOTAL SCORE</Typography>
                        <Typography variant="h4" fontWeight="1000" color="#10B981">372 / 400</Typography>
                    </Grid>
                </Grid>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& th': { border: 0, bgcolor: '#F8FAFC', color: '#64748B', fontWeight: '800', py: 2 } }}>
                                <TableCell sx={{ borderRadius: '12px 0 0 12px' }}>MODULE NAME</TableCell>
                                <TableCell align="center">THRESHOLD</TableCell>
                                <TableCell align="center">RAW SCORE</TableCell>
                                <TableCell align="right" sx={{ borderRadius: '0 12px 12px 0' }}>PROFICIENCY</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subjects.map((sub, i) => (
                                <TableRow key={i} sx={{ '& td': { borderBottom: '1px dashed #F1F5F9', py: 2.5 } }}>
                                    <TableCell sx={{ fontWeight: '1000', color: '#1E293B' }}>{sub.name}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: '800', color: '#94A3B8' }}>{sub.min}%</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: '1000', color: '#2563EB' }}>{sub.score} / {sub.max}</TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ width: 140, ml: 'auto' }}>
                                            <LinearProgress variant="determinate" value={sub.score} sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#10B981' } }} />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="subtitle1" fontWeight="1000">Semester Examination</Typography>
                        <Typography variant="caption" color="#64748B" fontWeight="800">Scheduling for 24th Oct, 2025 • 09:30 AM IST</Typography>
                    </Box>
                    <Button variant="contained" sx={{ bgcolor: '#2563EB', borderRadius: 3, textTransform: 'none', fontWeight: '1000' }}>Admit Card</Button>
                </Stack>
            </Paper>
        </Stack>
    );
};

const ContactIntelligence = () => (
    <Stack spacing={4}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0' }}>
            <SectionHeader title="Candidate Reach" />
            <Grid container spacing={3}>
                <Grid item size={{ xs: 12, md: 6 }}><DetailChip icon={<PhoneIcon />} label="Direct Line" value="+91 98765 43210" /></Grid>
                <Grid item size={{ xs: 12, md: 6 }}><DetailChip icon={<EmailIcon />} label="Enterprise Mail" value="yugendar.reddy@skilltech.com" /></Grid>
                <Grid item size={{ xs: 12 }}><DetailChip icon={<LocationIcon />} label="Co-ordinates" value="450 Briarwood Drive, Hyderabad, TS, 500032" /></Grid>
            </Grid>
        </Paper>

        <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0' }}>
            <SectionHeader title="Guardian Details" />
            <Grid container spacing={4}>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <Stack spacing={2}>
                        <Typography variant="caption" fontWeight="1000" color="#64748B">PRIMARY CONTACT (FATHER)</Typography>
                        <Typography variant="body2" fontWeight="1000">Mr. Somireddy</Typography>
                        <Typography variant="caption" color="#64748B" fontWeight="800">+91 99887 76655</Typography>
                    </Stack>
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <Stack spacing={2}>
                        <Typography variant="caption" fontWeight="1000" color="#64748B">SECONDARY CONTACT (MOTHER)</Typography>
                        <Typography variant="body2" fontWeight="1000">Mrs. Padma</Typography>
                        <Typography variant="caption" color="#64748B" fontWeight="800">+91 99887 76644</Typography>
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    </Stack>
);

const OJTMetrics = ({ studentId }) => {
    const [monitorings, setMonitorings] = useState([]);
    const [evaluation, setEvaluation] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const savedUser = sessionStorage.getItem('iti_company_user');
                let companyId = null;
                if (savedUser) { companyId = JSON.parse(savedUser).companyId; }
                const [monRes, evalRes] = await Promise.all([
                    getOJTMonitorings({ studentId }),
                    companyId ? getOJTEvaluation(studentId, companyId).catch(() => ({ success: false })) : Promise.resolve({ success: false })
                ]);
                if (monRes.success) setMonitorings(monRes.data || []);
                if (evalRes.success) setEvaluation(evalRes.data);
            } catch (error) { console.error(error); } finally { setLoading(false); }
        };
        fetchData();
    }, [studentId]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    return (
        <Stack spacing={4}>
            {evaluation && (
                <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                    <SectionHeader title="Performance Analytics" action={<Rating value={4.5} precision={0.5} readOnly />} />
                    <Grid container spacing={6} alignItems="center">
                        <Grid item size={{ xs: 12, md: 7 }}>
                            <Stack spacing={3}>
                                {[
                                    { label: 'Attendance Reliability', value: evaluation.attendanceMarks },
                                    { label: 'Disciplinary Record', value: evaluation.disciplineMarks },
                                    { label: 'Technical Proficiency', value: evaluation.technicalSkillsMarks },
                                    { label: 'Collaborative Potential', value: evaluation.teamworkMarks },
                                ].map((stat, i) => (
                                    <Box key={i}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="caption" fontWeight="1000" color="#475569">{stat.label}</Typography>
                                            <Typography variant="caption" fontWeight="1000" color="#2563EB">{stat.value * 10}%</Typography>
                                        </Stack>
                                        <LinearProgress variant="determinate" value={stat.value * 10} sx={{ height: 8, borderRadius: 4, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#2563EB' } }} />
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item size={{ xs: 12, md: 5 }}>
                            <Box sx={{ p: 4, bgcolor: '#F8FAFC', borderRadius: 8, textAlign: 'center', border: '1px solid #E2E8F0' }}>
                                <Typography variant="caption" fontWeight="1000" color="#64748B">OVERALL EFFICIENCY</Typography>
                                <Typography variant="h2" fontWeight="1000" color="#2563EB" sx={{ my: 2 }}>
                                    {evaluation.attendanceMarks + evaluation.disciplineMarks + evaluation.learningAbilityMarks + evaluation.technicalSkillsMarks + evaluation.teamworkMarks}
                                    <span style={{ fontSize: '1.2rem', color: '#94A3B8' }}>/50</span>
                                </Typography>
                                <Typography variant="body2" color="#64748B" fontWeight="800">Based on industry evaluation metrics</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                <SectionHeader title="Observation Log" />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& th': { border: 0, bgcolor: '#F8FAFC', color: '#64748B', fontWeight: '800' } }}>
                                <TableCell>DATE</TableCell>
                                <TableCell>STATUS</TableCell>
                                <TableCell>CORE OBSERVATIONS</TableCell>
                                <TableCell align="right">ASSESSOR</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {monitorings.map((log, i) => (
                                <TableRow key={i} sx={{ '& td': { borderBottom: '1px dashed #F1F5F9' } }}>
                                    <TableCell sx={{ fontWeight: '1000' }}>{new Date(log.visitDate).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <Chip label={log.attendanceStatus} size="small" variant="outlined" sx={{ fontWeight: 'bold', color: log.attendanceStatus === 'present' ? '#10B981' : '#F43F5E', borderColor: alpha(log.attendanceStatus === 'present' ? '#10B981' : '#F43F5E', 0.2) }} />
                                    </TableCell>
                                    <TableCell sx={{ color: '#475569', fontWeight: 'bold' }}>{log.activitiesObserved}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: '1000' }}>Lead Instructor</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Stack>
    );
};

const DetailedStudent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const student = location.state?.student || {};
    const [tabValue, setTabValue] = useState(0);

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, minHeight: '100vh', bgcolor: '#F8FAFC' }}>
            {/* Header section */}
            <Fade in={true}>
                <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: 3, '&:hover': { bgcolor: '#F1F5F9' } }}>
                            <BackIcon sx={{ color: '#0F172A' }} />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Trainee Archive</Typography>
                            <Typography variant="body2" color="#64748B" fontWeight="600">Enterprise Student Management System</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Button variant="outlined" startIcon={<EditIcon />} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: '1000', color: '#475569', borderColor: '#E2E8F0', bgcolor: 'white' }}>Edit Records</Button>
                        <Button variant="contained" startIcon={<AwardIcon />} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: '1000', bgcolor: '#0F172A', '&:hover': { bgcolor: '#1E293B' }, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>Certify Entry</Button>
                    </Stack>
                </Box>
            </Fade>

            <Grid container spacing={5}>
                {/* Profile Side */}
                <Grid item size={{ xs: 12, lg: 4 }}>
                    <Grow in={true} timeout={600}>
                        <Box><ProfileSidebar student={student} /></Box>
                    </Grow>
                </Grid>

                {/* Content Side */}
                <Grid item size={{ xs: 12, lg: 8 }}>
                    <Fade in={true} timeout={1000}>
                        <Paper elevation={0} sx={{ p: 1, mb: 4, borderRadius: 6, border: '1px solid #E2E8F0', bgcolor: 'white', display: 'inline-flex' }}>
                            <Tabs
                                value={tabValue}
                                onChange={(e, v) => setTabValue(v)}
                                sx={{
                                    minHeight: 48,
                                    '& .MuiTabs-indicator': { height: '100%', borderRadius: 4, bgcolor: alpha('#2563EB', 0.06), zIndex: 0 },
                                    '& .MuiTab-root': { px: 3, minHeight: 48, borderRadius: 4, fontWeight: '1000', fontSize: '0.8rem', color: '#64748B', zIndex: 1, transition: 'all 0.2s', '&.Mui-selected': { color: '#2563EB' } }
                                }}
                            >
                                <Tab label="Performance" />
                                <Tab label="Reachability" />
                                <Tab label="Archive" />
                                <Tab label="OJT Analytics" />
                            </Tabs>
                        </Paper>
                    </Fade>

                    <Box sx={{ mt: 2 }}>
                        {tabValue === 0 && <Grow in={true}><Box><ExamsAndResults /></Box></Grow>}
                        {tabValue === 1 && <Grow in={true}><Box><ContactIntelligence /></Box></Grow>}
                        {tabValue === 2 && <Grow in={true}><Box>
                            <Stack spacing={2}>
                                {['Academic_Transcripts.pdf', 'Govt_ID_Proof.pdf'].map((doc, i) => (
                                    <Paper key={i} elevation={0} sx={{ p: 3, borderRadius: 5, border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: alpha('#2563EB', 0.1), color: '#2563EB' }}><DocIcon /></Avatar>
                                            <Typography variant="body2" fontWeight="1000">{doc}</Typography>
                                        </Stack>
                                        <Button startIcon={<FileDownloadIcon />} sx={{ fontWeight: '1000', textTransform: 'none' }}>Download</Button>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box></Grow>}
                        {tabValue === 3 && <Grow in={true}><Box><OJTMetrics studentId={student.id} /></Box></Grow>}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DetailedStudent;
